import { Dose, PhiV2, UsageRule, RoundProductOverride } from '../services/sprayService';

/**
 * Frontend mirror of backend/src/utils/sprayApplicationResolver.js.
 * Keep the logic in lock-step; cross-runtime drift is the most common source of resolver bugs.
 *
 * Resolution order:
 *   1. roundProductEntry.doseOverride / phiOverride (each field independent)
 *   2. matching usageRules on the product — max dose + max PHI across matches
 *   3. legacy scalar fallback (transitional; removed once all docs migrate)
 *   4. unresolved → null fields + warnings
 */

export type ResolvedFrom = 'round_override' | 'product_rule' | 'manual' | 'unresolved';

export interface MatchedRule {
  ruleId: any;             // ObjectId-like; string on serialised payload, opaque to UI
  crops: string[];
  pests: string[];
  dose?: Dose;
  phi?: PhiV2;
}

export interface ResolvedSprayApplication {
  dose: Dose | null;
  phi: PhiV2 | null;
  resolvedFrom: ResolvedFrom;
  matchedRules: MatchedRule[];
  doseSourceRuleId: any | null;
  phiSourceRuleId: any | null;
  coveredPests: string[];
  unmatchedPests: string[];
  warnings: string[];
}

export interface ResolveArgs {
  product: {
    productName?: string;
    usageRules?: UsageRule[];
    // Legacy fields (transitional fallback)
    dosePer100L?: number;
    dosePer100LUnit?: string;
    phi?: { type?: string; value?: string | number };
    pestTreated?: string;
  } | null | undefined;
  roundProductEntry?: RoundProductOverride | null;
  orchard: { cropType?: string } | null | undefined;
  targetPests: string[];
  round?: { waterPerHectare?: number } | null;
  cropLineage?: string[];
}

export function resolveSprayApplication(args: ResolveArgs): ResolvedSprayApplication {
  const { product, roundProductEntry, orchard, targetPests, round, cropLineage } = args;
  const warnings: string[] = [];
  const pests = Array.isArray(targetPests) ? targetPests.filter(Boolean) : [];
  // Lineage drives ancestry matching. Fall back to a singleton lineage built
  // from orchard.cropType so callers that have not been updated keep exact-match
  // behavior.
  const lineage = Array.isArray(cropLineage) && cropLineage.length
    ? cropLineage
    : (orchard?.cropType ? [orchard.cropType] : []);

  let dose: Dose | null = null;
  let phi: PhiV2 | null = null;
  let resolvedFrom: ResolvedFrom = 'unresolved';
  let matchedRules: MatchedRule[] = [];
  let doseSourceRuleId: any = null;
  let phiSourceRuleId: any = null;
  let coveredPests: string[] = [];
  let unmatchedPests: string[] = pests.slice();

  // Step 1: Round overrides. Each field resolves independently.
  if (roundProductEntry?.doseOverride) {
    dose = pickPlainDose(roundProductEntry.doseOverride);
    resolvedFrom = 'round_override';
  }
  if (roundProductEntry?.phiOverride) {
    phi = pickPlainPhi(roundProductEntry.phiOverride);
    resolvedFrom = 'round_override';
  }

  // Step 2: Product rule matching (only fills fields that round-override did not).
  if (!dose || !phi) {
    const matches = (product?.usageRules || []).filter(r =>
      ruleMatchesCrop(r, lineage) && ruleMatchesAnyPest(r, pests)
    );

    if (matches.length > 0) {
      // Snapshot every match so audit can show full reasoning.
      matchedRules = matches.map(r => ({
        ruleId: r._id,
        crops:  r.crops || [],
        pests:  r.pests || [],
        dose:   r.dose ? pickPlainDose(r.dose) ?? undefined : undefined,
        phi:    r.phi  ? pickPlainPhi(r.phi)  ?? undefined : undefined,
      }));

      const denominators = new Set(
        matches.filter(r => r.dose).map(r => (r.dose as Dose).denominator)
      );
      if (denominators.size > 1) {
        warnings.push(`mixed_denominator: matched rules span ${Array.from(denominators).join(', ')}`);
      }

      coveredPests = pests.filter(p =>
        matches.some(r => (r.pests || []).length === 0 || (r.pests || []).includes(p))
      );
      unmatchedPests = pests.filter(p => !coveredPests.includes(p));

      if (!dose) {
        const picked = pickMaxDose(mostSpecificWith(matches, lineage, 'dose'), round);
        if (picked) {
          dose = picked.dose;
          doseSourceRuleId = picked.ruleId;
          if (resolvedFrom !== 'round_override') resolvedFrom = 'product_rule';
        }
      }
      if (!phi) {
        const picked = pickMaxPhi(mostSpecificWith(matches, lineage, 'phi'));
        if (picked) {
          phi = picked.phi;
          phiSourceRuleId = picked.ruleId;
          if (resolvedFrom !== 'round_override') resolvedFrom = 'product_rule';
        }
      }
    }

    if (unmatchedPests.length > 0) {
      warnings.push(`unmatched_pests:${unmatchedPests.join(',')}`);
    }
  } else {
    // Round overrides covered everything; matched-pest accounting still applies.
    coveredPests   = pests.slice();
    unmatchedPests = [];
  }

  // Step 3: Legacy scalar fallback. Transitional only; removed in the
  // follow-up release that drops the four legacy scalars.
  if (!dose && !phi && (!product?.usageRules || product.usageRules.length === 0)) {
    const synth = synthesiseLegacyRule(product);
    if (synth && (synth.dose || synth.phi)) {
      const wildcardPestOk = !synth.pests.length || synth.pests.some(p => pests.includes(p));
      if (wildcardPestOk) {
        if (synth.dose) {
          dose = synth.dose;
          doseSourceRuleId = null;
        }
        if (synth.phi) {
          phi = synth.phi;
          phiSourceRuleId = null;
        }
        resolvedFrom = 'product_rule';
        warnings.push('legacy-scalar: resolved from deprecated product scalars; backfill usageRules');
        coveredPests   = pests.slice();
        unmatchedPests = [];
      }
    }
  }

  if (resolvedFrom === 'unresolved' && warnings.length === 0) {
    warnings.push('no_matching_rule_or_override');
  }

  return {
    dose,
    phi,
    resolvedFrom,
    matchedRules,
    doseSourceRuleId,
    phiSourceRuleId,
    coveredPests,
    unmatchedPests,
    warnings,
  };
}

function pickPlainDose(d: Dose | undefined): Dose | null {
  if (!d) return null;
  const out: Dose = { value: d.value, unit: d.unit, denominator: d.denominator };
  if (d.valueMin != null) out.valueMin = d.valueMin;
  if (d.valueMax != null) out.valueMax = d.valueMax;
  return out;
}

function pickPlainPhi(p: PhiV2 | undefined): PhiV2 | null {
  if (!p) return null;
  return { value: p.value, unit: p.unit };
}

function ruleMatchesCrop(rule: UsageRule, lineage: string[]): boolean {
  const crops = rule.crops || [];
  if (crops.length === 0) return true;            // crop-wildcard
  if (!lineage.length) return false;
  return crops.some(c => lineage.includes(c));
}

// 0 = exact leaf, larger = broader ancestor, lineage.length = crop-wildcard.
function cropSpecificity(rule: UsageRule, lineage: string[]): number {
  const crops = rule.crops || [];
  if (crops.length === 0) return lineage.length;
  let best = Infinity;
  for (const c of crops) { const idx = lineage.indexOf(c); if (idx >= 0 && idx < best) best = idx; }
  return best === Infinity ? lineage.length : best;
}

// Keep only the rules at the most-specific level among those carrying `field`.
function mostSpecificWith(rules: UsageRule[], lineage: string[], field: 'dose' | 'phi'): UsageRule[] {
  const withField = rules.filter(r => (r as any)[field]);
  if (!withField.length) return [];
  const scored = withField.map(r => ({ r, s: cropSpecificity(r, lineage) }));
  const min = Math.min(...scored.map(x => x.s));
  return scored.filter(x => x.s === min).map(x => x.r);
}

function ruleMatchesAnyPest(rule: UsageRule, pests: string[]): boolean {
  const rp = rule.pests || [];
  if (rp.length === 0) return true;                // rule wildcard: applies to any pest
  if (!pests || pests.length === 0) return true;   // no pest named on the application: treat as "any" (#291)
  return rp.some(p => pests.includes(p));
}

function pickMaxDose(
  rules: UsageRule[],
  round?: ResolveArgs['round'],
): { dose: Dose; ruleId: any } | null {
  // Bucket rules by denominator
  const buckets = new Map<string, UsageRule[]>();
  for (const r of rules) {
    if (!r.dose) continue;
    const key = r.dose.denominator;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(r);
  }
  if (buckets.size === 0) return null;

  const preferredOrder = (round?.waterPerHectare ?? 0) > 0 ? ['100L', 'ha'] : ['ha', '100L'];
  for (const denom of preferredOrder) {
    if (buckets.has(denom)) {
      let best: { dose: Dose; ruleId: any } | null = null;
      for (const r of buckets.get(denom)!) {
        if (!best || (r.dose as Dose).value > best.dose.value) {
          best = { dose: pickPlainDose(r.dose)!, ruleId: r._id || r };
        }
      }
      return best;
    }
  }
  return null;
}

function pickMaxPhi(rules: UsageRule[]): { phi: PhiV2; ruleId: any } | null {
  let best: { phi: PhiV2; ruleId: any; normalised: number } | null = null;
  for (const r of rules) {
    if (!r.phi) continue;
    const normalised = phiInDays(r.phi);
    if (normalised == null) continue;
    if (!best || normalised > best.normalised) {
      best = { phi: pickPlainPhi(r.phi)!, normalised, ruleId: r._id || r };
    }
  }
  return best ? { phi: best.phi, ruleId: best.ruleId } : null;
}

function phiInDays(phi: PhiV2): number | null {
  if (phi.unit === 'days') return phi.value;
  if (phi.unit === 'months') return phi.value * 30;
  return null;
}

function synthesiseLegacyRule(
  product: ResolveArgs['product'],
): { dose: Dose | null; phi: PhiV2 | null; pests: string[] } | null {
  if (!product) return null;
  const dose: Dose | null = product.dosePer100L != null && product.dosePer100LUnit
    ? { value: product.dosePer100L, unit: product.dosePer100LUnit as any, denominator: '100L' as const }
    : null;

  let phi: PhiV2 | null = null;
  if (product.phi && product.phi.type && product.phi.value != null) {
    const numericVal = Number(product.phi.value);
    if (product.phi.type === 'days' && Number.isFinite(numericVal)) {
      phi = { value: numericVal, unit: 'days' };
    } else if (Number.isFinite(numericVal)) {
      // month_of_year carries a numeric "month-of-year"; we pass through.
      phi = { value: numericVal, unit: product.phi.type as any };
    } else {
      // petal_drop_80 with a non-numeric string value (e.g. "80% Petal Drop")
      phi = { value: 0, unit: product.phi.type as any };
    }
  }

  return { dose, phi, pests: product.pestTreated ? [product.pestTreated] : [] };
}
